'use client';

import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';

import { BrandIcon } from '@/components/brand-icon';

const LENGTH = 6;

/** Insere `char` no índice e devolve a string compactada (sem espaços). */
function setCharAtPure(value: string, index: number, char: string): string {
  const chars = (value + ' '.repeat(LENGTH)).slice(0, LENGTH).split('');
  chars[index] = char || ' ';
  return chars.join('').replace(/ /g, '').slice(0, LENGTH);
}

interface OtpVerifyCardProps {
  email: string;
  value: string;
  onChange: (next: string) => void;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  resendIn: number;
  loading?: boolean;
  error?: string | null;
  /**
   * Quando 'mfa', o card vira "Verificação em dois passos" e esconde o botão
   * de reenviar (TOTP é offline, sempre disponível no app authenticator).
   * Default 'email' — fluxo padrão de OTP via e-mail.
   */
  mode?: 'email' | 'mfa';
}

/**
 * OTP card · estrutura fiel ao thanh/otp-verify (21st.dev) com brand Trakd:
 * - rounded-3xl shadow-2xl overflow-hidden centrado em h-screen
 * - background: gradiente brand Trakd + orbs ambient + granulado
 * - 6 inputs w-14 h-14 rounded-2xl bg-white/10 border-white/20
 * - "Não recebeu? Reenviar"
 */
export function OtpVerifyCard({
  email,
  value,
  onChange,
  onSubmit,
  onResend,
  onBack,
  resendIn,
  loading,
  error,
  mode = 'email',
}: OtpVerifyCardProps) {
  const isMfa = mode === 'mfa';
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  // State interno é a fonte da verdade. Sync com prop `value` num useEffect
  // (one-way, parent → interno) cobre o caso de erro onde o parent limpa
  // (setMfaCode('')) — o card limpa junto. Sem isso, handlers liam `value`
  // do closure de cada render: autofill de gerenciador de senha que dispara
  // múltiplos `change` events antes de um re-render via cada handler com
  // `value` stale, e o auto-submit nunca dispara porque `next.length` nunca
  // chega a 6.
  const [innerValue, setInnerValue] = useState(value);
  const submittedRef = useRef('');

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  // Auto-submit quando completar 6 dígitos. `submittedRef` evita disparar duas
  // vezes pro mesmo código (ex: re-render por loading flip). Quando o parent
  // limpa após erro, o reset do submittedRef no useEffect [value] permite
  // retry com novo código.
  useEffect(() => {
    if (innerValue.length === LENGTH && submittedRef.current !== innerValue && !loading) {
      submittedRef.current = innerValue;
      onSubmit(innerValue);
    }
    if (innerValue.length === 0) {
      submittedRef.current = '';
    }
  }, [innerValue, loading, onSubmit]);

  function update(updater: (prev: string) => string) {
    setInnerValue((prev) => {
      const next = updater(prev);
      onChange(next);
      return next;
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      update((prev) => setCharAtPure(prev, index, ''));
      return;
    }
    if (raw.length > 1) {
      let nextLen = 0;
      update((prev) => {
        const next = (prev.slice(0, index) + raw).replace(/\D/g, '').slice(0, LENGTH);
        nextLen = next.length;
        return next;
      });
      // Focus depois do setState (queued) — usa rAF pra rodar pós-flush
      requestAnimationFrame(() => {
        refs.current[Math.min(nextLen, LENGTH - 1)]?.focus();
      });
      return;
    }
    update((prev) => setCharAtPure(prev, index, raw));
    if (index < LENGTH - 1) refs.current[index + 1]?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.key === 'Backspace' && !innerValue[index] && index > 0) {
      e.preventDefault();
      update((prev) => setCharAtPure(prev, index - 1, ''));
      refs.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < LENGTH - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!pasted) return;
    e.preventDefault();
    update(() => pasted);
    refs.current[Math.min(pasted.length, LENGTH - 1)]?.focus();
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96, filter: 'blur(8px)' }}
        animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl"
      >
        {/* Background = mesma DNA do squircle do ícone (brandbook §grad-bg-icon):
            base dark profunda + glow violeta/fúchsia + granulado denso. */}
        <div className="bg-trakd-icon absolute inset-0 z-0">
          <div
            aria-hidden
            className="animate-orb-1 absolute -top-20 -right-16 h-[22rem] w-[22rem] rounded-full blur-3xl"
            style={{ background: 'rgba(144, 97, 255, 0.55)' }}
          />
          <div
            aria-hidden
            className="animate-orb-2 absolute -bottom-20 -left-16 h-[22rem] w-[22rem] rounded-full blur-3xl"
            style={{ background: 'rgba(159, 17, 206, 0.38)' }}
          />
          <div
            aria-hidden
            className="absolute top-1/3 left-1/2 h-[12rem] w-[24rem] -translate-x-1/2 rounded-[50%] blur-3xl"
            style={{ background: 'rgba(166, 53, 149, 0.22)' }}
          />
          {/* Granulado denso (mesmo SVG noise do brandbook L65) */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.22] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.87 0 0 0 0 0.86 0 0 0 0 0.96 0 0 0 0.55 0'/></filter><rect width='220' height='220' filter='url(%23n)' opacity='0.18'/></svg>\")",
            }}
          />
        </div>

        <div className="relative z-10 px-8 py-14">
          <div className="mb-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto mb-6 w-fit"
            >
              <BrandIcon size={56} className="rounded-2xl" alt="Trakd" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-ink mb-3 text-2xl font-semibold tracking-tight"
            >
              {isMfa ? 'Verificação em dois passos' : 'Confirme seu acesso'}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-ink/70 text-sm leading-relaxed"
            >
              {isMfa ? (
                <>
                  Digite o código de 6 dígitos do seu app
                  <br />
                  authenticator (Google Authenticator, 1Password, Authy).
                </>
              ) : (
                <>
                  Enviamos um código de 6 dígitos para
                  <br />
                  <span className="text-ink font-medium">{email}</span>
                </>
              )}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8 flex justify-center gap-2.5 sm:gap-3"
            role="group"
            aria-label="Código de 6 dígitos"
          >
            {Array.from({ length: LENGTH }).map((_, i) => (
              <input
                key={i}
                ref={(el) => {
                  refs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={LENGTH}
                value={innerValue[i] ?? ''}
                onChange={(e) => handleChange(e, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                disabled={loading}
                aria-label={`Dígito ${i + 1}`}
                className="text-ink placeholder-ink/40 focus:border-violet/70 focus:bg-violet/10 focus:shadow-trakd-glow-violet h-14 w-12 rounded-2xl border border-white/15 bg-white/[0.06] text-center text-xl font-semibold opacity-100 shadow-lg backdrop-blur-sm transition-all duration-200 focus:outline-none sm:w-14"
              />
            ))}
          </motion.div>

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 rounded-xl bg-red-500/20 px-3 py-2 text-center text-sm text-red-100"
              role="alert"
            >
              {error}
            </motion.p>
          )}

          {!isMfa && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-8 text-center"
            >
              <span className="text-ink/60 text-sm">Não recebeu o código? </span>
              <button
                type="button"
                onClick={onResend}
                disabled={resendIn > 0 || loading}
                className="text-ink/85 hover:text-ink text-sm font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar'}
              </button>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center"
          >
            <button
              type="button"
              onClick={onBack}
              className="text-ink/55 hover:text-ink/85 inline-flex items-center gap-1.5 text-xs transition-colors"
            >
              <FiArrowLeft className="h-3.5 w-3.5" /> Voltar ao login
            </button>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
